import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { events } from "@/mocks/events";
import StatusBadge from "@/components/base/StatusBadge";
import EventCard from "@/components/feature/EventCard";
import SectionHeading from "@/components/base/SectionHeading";
import { formatDateCN, getWeekday, shareContent } from "@/utils/format";
import usePageMeta from "@/hooks/usePageMeta";

export default function EventDetail() {
  const { eventId } = useParams();
  const [toast, setToast] = useState("");
  const event = events.find((e) => e.id === eventId);

  usePageMeta(
    event ? `${event.title} 比赛详情 - 51每日大赛` : "赛事信息不可用 - 51每日大赛",
    event
      ? `${event.title} 将于 ${formatDateCN(event.date)} ${event.time} 在 ${event.venue} 进行。${event.summary}`
      : "该赛事信息不可用或已结束，请返回赛事列表查看其他比赛。",
    event ? `${event.title},${event.categoryName},${event.subCategory},比赛结果,51每日大赛` : "赛事详情,51每日大赛",
  );

  const handleShare = async () => {
    if (!event) return;
    const res = await shareContent(event.title, event.summary, window.location.href);
    if (res === "copied") setToast("链接已复制到剪贴板");
    else if (res === "shared") setToast("感谢分享！");
    else setToast("分享失败，请手动复制链接");
    setTimeout(() => setToast(""), 2200);
  };

  if (!event) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <i className="ri-error-warning-line text-5xl text-foreground-300" />
        <h1 className="mt-4 font-heading text-xl text-foreground-900">赛事信息不可用或已结束</h1>
        <p className="mt-2 text-sm text-foreground-600">您访问的比赛可能已被移除或链接有误。</p>
        <Link
          to="/events"
          className="inline-flex items-center gap-2 mt-6 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          返回赛事列表
        </Link>
      </div>
    );
  }

  const isFinished = event.status === "finished";
  const homeWin = isFinished && (event.homeScore ?? 0) > (event.awayScore ?? 0);
  const awayWin = isFinished && (event.awayScore ?? 0) > (event.homeScore ?? 0);
  const related = events.filter((e) => e.categoryId === event.categoryId && e.id !== event.id).slice(0, 3);

  return (
    <div className="w-full">
      <div className="w-full bg-background-50 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${event.categoryId}`} className="hover:text-primary-600 transition-colors">
              {event.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800 truncate max-w-[200px]">{event.title}</span>
          </nav>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="px-2.5 py-0.5 rounded-md bg-primary-100 text-primary-800 text-xs font-label">{event.categoryName}</span>
              <span className="px-2.5 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-xs font-label">{event.subCategory}</span>
              <span className="px-2.5 py-0.5 rounded-md bg-accent-100 text-accent-900 text-xs font-label">{event.level}</span>
              <StatusBadge status={event.status} />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 leading-tight">
              {event.title}
            </h1>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              <div className="relative h-44 md:h-64">
                <img src={event.cover} alt={event.title} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/70 to-transparent" />
              </div>
              <div className="p-6 md:p-8 -mt-16 relative">
                <div className="rounded-lg border border-background-200 bg-background-50 p-6 flex items-center justify-between gap-4">
                  <div className="flex-1 flex flex-col items-center gap-3 text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden border border-background-200 bg-background-100">
                      <img src={event.home.logo} alt={event.home.name} className="w-full h-full object-cover" />
                    </div>
                    <span className={`text-sm md:text-base ${homeWin ? "font-semibold text-foreground-950" : "text-foreground-700"}`}>
                      {event.home.name}
                    </span>
                  </div>

                  <div className="shrink-0 flex flex-col items-center">
                    {event.status === "upcoming" ? (
                      <>
                        <span className="font-heading text-2xl md:text-3xl text-foreground-950">{event.time}</span>
                        <span className="text-xs text-foreground-500 mt-1">比赛未开始</span>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-3 font-heading text-4xl md:text-5xl">
                          <span className={homeWin ? "text-primary-600" : "text-foreground-800"}>{event.homeScore}</span>
                          <span className="text-foreground-300 text-2xl">:</span>
                          <span className={awayWin ? "text-primary-600" : "text-foreground-800"}>{event.awayScore}</span>
                        </div>
                        <span className="text-xs text-foreground-500 mt-1">{isFinished ? "最终比分" : "实时比分"}</span>
                      </>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col items-center gap-3 text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden border border-background-200 bg-background-100">
                      <img src={event.away.logo} alt={event.away.name} className="w-full h-full object-cover" />
                    </div>
                    <span className={`text-sm md:text-base ${awayWin ? "font-semibold text-foreground-950" : "text-foreground-700"}`}>
                      {event.away.name}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                  <div className="flex items-center gap-2 text-sm text-foreground-700">
                    <i className="ri-calendar-line text-primary-500 text-lg" />
                    <span>{formatDateCN(event.date)} {getWeekday(event.date)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground-700">
                    <i className="ri-time-line text-primary-500 text-lg" />
                    <span>{event.time}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground-700">
                    <i className="ri-map-pin-line text-primary-500 text-lg" />
                    <span className="truncate">{event.venue}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="font-heading text-lg text-foreground-950 mb-3">赛事简介</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{event.summary}</p>
            </div>

            <div className="mt-8">
              <h2 className="font-heading text-lg text-foreground-950 mb-3">赛况回顾</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{event.recap}</p>
            </div>

            <div className="mt-8 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500">赛事标签：</span>
              {event.tags.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">{t}</span>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base text-foreground-950 mb-4">分享这场比赛</h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line" /> 分享
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  aria-label="复制链接"
                  className="w-10 h-10 rounded-md border border-background-300 text-foreground-700 flex items-center justify-center hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-link" />
                </button>
              </div>
              <p className="mt-3 text-xs text-foreground-500 leading-relaxed">
                支持调用系统分享或复制链接，方便分享给好友。
              </p>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base text-foreground-950 mb-4">赛事信息</h2>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">赛事项目</dt>
                  <dd className="text-foreground-900 text-right">{event.categoryName}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">子分类</dt>
                  <dd className="text-foreground-900 text-right">{event.subCategory}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">赛事等级</dt>
                  <dd className="text-foreground-900 text-right">{event.level}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-foreground-500">比赛地点</dt>
                  <dd className="text-foreground-900 text-right">{event.venue}</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>

        {related.length > 0 ? (
          <div className="mt-12">
            <SectionHeading eyebrow="Related" title="相关比赛推荐" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((e) => (
                <EventCard key={e.id} event={e} />
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg bg-secondary-900 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      ) : null}
    </div>
  );
}